/**
 * Find & match a director against a completed Sumsub ID/AML check
 * (UK workspace). Selecting a result pulls DOB, address, phone and
 * email back into the Onboarding wizard's director fields.
 */
import { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Fingerprint, Search, Loader2, MapPin, Mail, Phone, Calendar } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const answerBadge = (answer) => {
  if (answer === "GREEN") return <Badge className="bg-green-100 text-green-800 border-green-300">GREEN</Badge>;
  if (answer === "RED") return <Badge className="bg-red-100 text-red-700 border-red-300">RED</Badge>;
  return <Badge variant="outline" className="text-amber-700 border-amber-300">{answer || "pending"}</Badge>;
};

// CH director names come as "SURNAME, Forename" — search by surname for best recall
const defaultQuery = (name) => (name || "").includes(",") ? name.split(",")[0].trim() : (name || "").trim();

export default function SumsubMatchDialog({ open, onOpenChange, directorName, onMatch }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/contracts/sumsub/search?q=${encodeURIComponent(q.trim())}&limit=10`);
      setResults(data?.applicants || []);
      setSearched(true);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Search failed");
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      const q = defaultQuery(directorName);
      setQuery(q);
      setResults([]);
      setSearched(false);
      setError(null);
      if (q) runSearch(q);
    }
  }, [open, directorName, runSearch]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sumsub-match-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5 text-indigo-600" />
            Find Sumsub ID Check
          </DialogTitle>
          <DialogDescription>
            Match <span className="font-medium text-foreground">{directorName}</span> to their completed
            Sumsub check — DOB, address, phone &amp; email will be pulled in automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            placeholder="Search by name, email or phone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch(query)}
            data-testid="sumsub-search-input"
          />
          <Button onClick={() => runSearch(query)} disabled={loading || query.trim().length < 2}
                  data-testid="sumsub-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="space-y-2">
          {results.map((a) => (
            <div key={a.applicant_id}
                 className="border rounded-lg p-3 flex items-start justify-between gap-3 hover:bg-slate-50"
                 data-testid="sumsub-result-row">
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{a.full_name || "(no name)"}</span>
                  {answerBadge(a.review_answer)}
                  {a.level_name && (
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{a.level_name}</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                  {a.dob && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{a.dob}</span>}
                  {a.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{a.email}</span>}
                  {a.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.phone}</span>}
                </div>
                {a.address && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3 shrink-0" />{a.address}
                  </div>
                )}
              </div>
              <Button size="sm" onClick={() => onMatch(a)} data-testid="sumsub-match-btn">
                Match
              </Button>
            </div>
          ))}
          {searched && !loading && results.length === 0 && !error && (
            <p className="text-sm text-muted-foreground text-center py-6">
              No Sumsub checks found for “{query}” — try a different spelling, or search by email/phone.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
